import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { FiArrowDownLeft, FiArrowUpRight, FiCreditCard, FiDollarSign, FiHash, FiPhone } from 'react-icons/fi';
import { api, normalizeToken, tokenPath } from '../helpers';
import { API_BASE_URL } from '../helpers/config';
import { pageTransition, staggerContainer, staggerItem, tapScale, tapScaleTransition } from '../helpers/animations';
import { ProfileSkeleton } from '../components/skeletons';

function Account() {
  const { token: tokenParam } = useParams();
  const token = normalizeToken(tokenParam);
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!token) {
        toast.error('Session expired. Please open your access link again.');
        navigate('/', { replace: true });
        return;
      }
      setLoadingProfile(true);
      try {
        const { data } = await api.post(`${API_BASE_URL}/exchange/profile`, { token });
        if (data?.error === false && data?.data) {
          setProfile(data.data);
        } else if (data?.error === 'session expired') {
          toast.error('Session expired. Please open your access link again.');
          navigate('/', { replace: true });
        } else {
          toast.error(data?.error || 'Failed to fetch profile');
        }
      } catch {
        // api helper shows toast
      } finally {
        setLoadingProfile(false);
      }
    };

    fetchProfile();
  }, [navigate, token]);

  const goToBuyReport = () => navigate(tokenPath(token, 'buy-report'));
  const goToSellReport = () => navigate(tokenPath(token, 'sell-report'));
  const goToBankAccounts = () => navigate(tokenPath(token, 'bank-accounts'));

  const displayName = profile?.name || profile?.username || '—';
  const avatarLetter = displayName !== '—' ? displayName.charAt(0).toUpperCase() : '?';

  return (
    <motion.div
      {...pageTransition}
      transition={{ duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] }}
      style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}
    >
      <motion.h1 className="screen-title" variants={staggerItem} initial="initial" animate="animate">
        Account
      </motion.h1>

      <motion.div
        className="profile-card"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05, duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
      >
        {loadingProfile ? (
          <ProfileSkeleton />
        ) : profile ? (
          <>
            <div className="profile-card-top">
              <motion.div
                className="profile-avatar"
                initial={{ scale: 0.85, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.12, duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
              >
                {avatarLetter}
              </motion.div>
              <div className="profile-info">
                <div className="profile-name">{displayName}</div>
                <div className="profile-meta">
                  <span className="profile-meta-item">
                    <FiPhone size={12} aria-hidden />
                    {profile.mobile || '—'}
                  </span>
                  <span className="profile-meta-dot" aria-hidden />
                  <span className="profile-meta-item">
                    <FiHash size={12} aria-hidden />
                    {profile.user_id}
                  </span>
                </div>
              </div>
            </div>

            <motion.div
              className="profile-balance"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.18, duration: 0.3 }}
            >
              <span className="profile-balance-icon" aria-hidden>
                <FiDollarSign size={18} />
              </span>
              <div className="profile-balance-text">
                <span className="profile-balance-label">Available</span>
                <span className="profile-balance-value">
                  {Number(profile.usdt ?? 0).toFixed(2)}
                  <span className="profile-balance-unit">USDT</span>
                </span>
              </div>
            </motion.div>
          </>
        ) : (
          <p className="form-hint" style={{ margin: 0, textAlign: 'center' }}>
            Failed to load profile
          </p>
        )}
      </motion.div>

      <motion.div
        className="card list-card"
        variants={staggerContainer}
        initial="initial"
        animate="animate"
      >
        <motion.button
          type="button"
          className="list-item"
          onClick={goToBankAccounts}
          variants={staggerItem}
          whileTap={tapScale}
          transition={tapScaleTransition}
        >
          <FiCreditCard size={16} />
          <span>Bank accounts</span>
        </motion.button>
        <motion.button
          type="button"
          className="list-item"
          onClick={goToBuyReport}
          variants={staggerItem}
          whileTap={tapScale}
          transition={tapScaleTransition}
        >
          <FiArrowDownLeft size={16} />
          <span>Buy report</span>
        </motion.button>
        <motion.button
          type="button"
          className="list-item"
          onClick={goToSellReport}
          variants={staggerItem}
          whileTap={tapScale}
          transition={tapScaleTransition}
        >
          <FiArrowUpRight size={16} />
          <span>Sell report</span>
        </motion.button>
      </motion.div>
    </motion.div>
  );
}

export default Account;
